import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxfWatchlistV1OfacMatchQueryServiceOfacMatchGet } from '@interxfi/api-client/generated/hooks/ixf-watchlist-v1-ofac-match-query-service-ofac-match-get';
import {
  EntityActionMenu,
  type EntityActionMenuConfig,
  type EntityActionMenuItem,
} from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import XLargeIcon from '@interxfi/design-system/icons/react/icon-x-large';
import { IxfWatchlistV1ListType, type IxfWatchlistV1OfacMatchState, IxfWatchlistV1OfacMatchStatus } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { OfacMatchEventDrawer, OfacMatchEventSummary } from '../../../../../../../../../../features/watchlist/ofac/match/ofac-match-event';
import { getOfacMatchStateListItems } from '../../../../../../../../../../features/watchlist/ofac/match/ofac-match-list-items';
import { getOfacMatchDetailPath } from '../../../../../../../../../../features/watchlist/ofac/match/ofac-match-routing';
import { OfacMatchSpecTable } from '../../../../../../../../../../features/watchlist/ofac/match/ofac-match-spec-table';
import { WatchlistOfacMatchStatusPill } from '../../../../../../../../../../features/watchlist/ofac/match/ofac-match-status-pill';
import { WatchlistMatchCloseConfirmDialog } from '../../../../../../../../../../features/watchlist/watchlist-match-close-confirm-dialog';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/ofac/match/_match/$ofacMatchId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `ofac-match-${params.ofacMatchId}`,
        label: 'Match',
        to: getOfacMatchDetailPath(params.ofacMatchId),
        suffix: (
          <CopyButton
            content={params.ofacMatchId}
            label="Copy OFAC Match ID"
            tooltip={{ copy: params.ofacMatchId, copied: 'Copied OFAC Match ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('OFAC Match'),
  component: OfacMatchDetail,
});

function getMenuItems(ofacMatchId: string, status: IxfWatchlistV1OfacMatchStatus | undefined): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: OfacMatchEventDrawer,
        props: { request: { ofacMatchId } },
      },
    },
    actions:
      status === IxfWatchlistV1OfacMatchStatus.Opened
        ? ([
            {
              id: 'close',
              icon: <XLargeIcon aria-hidden />,
              label: 'Close Match',
              tone: 'danger-dk',
              interaction: {
                type: 'modal',
                modal: {
                  Component: WatchlistMatchCloseConfirmDialog,
                  props: { matchId: ofacMatchId, listType: IxfWatchlistV1ListType.Ofac },
                },
              },
              requiresWrite: true,
            },
          ] as const satisfies EntityActionMenuItem[])
        : undefined,
  };
}

function getListItems(data: IxfWatchlistV1OfacMatchState | undefined) {
  const items = getOfacMatchStateListItems(data);

  return {
    left: [items.profileId] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.ofacMatchId] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load OFAC match', 'view this OFAC match');

function OfacMatchDetail() {
  const { ofacMatchId } = Route.useParams();
  const request = { ofacMatchId } as const;
  const { data, error, isPending } = useIxfWatchlistV1OfacMatchQueryServiceOfacMatchGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.ofacMatch), [data?.ofacMatch]);
  const menuItems = useMemo(() => getMenuItems(ofacMatchId, data?.ofacMatch?.status), [ofacMatchId, data?.ofacMatch?.status]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <WatchlistOfacMatchStatusPill isPending={showPending} size="large" status={data?.ofacMatch?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <OfacMatchEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Matched Entries</Typography.Level3>

          <OfacMatchSpecTable loadingRows={2} specs={data?.ofacMatch?.data?.spec ?? STABLE_EMPTY_ARRAY_REF} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
